// FaceLandmarker wrapper.
//
// Sub-agent (P1.1) contract:
//   const tracker = new FaceTracker({ camera });
//   await tracker.load();          // downloads + warms model
//   tracker.start();               // begins per-frame inference
//   const f = tracker.lastFrame;   // most recent FaceFrame (or emptyFace())
//
// Implementation hints:
//   - Use `@mediapipe/tasks-vision` FaceLandmarker
//   - runningMode: 'VIDEO', numFaces: 1, outputFaceBlendshapes: false
//   - detectForVideo(camera.video, performance.now()) on each onFrame()
//   - Convert normalized landmarks → canvas px using camera.width/height
//   - Mouth-open hysteresis: openThreshold 0.045, closeThreshold 0.030
//   - Smile hysteresis: smileEnter 0.42, smileExit 0.36
//   - Inner-lip indices: [78, 95, 88, 178, 87, 14, 317, 402, 318, 324,
//                        308, 415, 310, 311, 312, 13, 82, 81, 80, 191]
//   - Outer-lip indices: [61, 146, 91, 181, 84, 17, 314, 405, 321, 375,
//                        291, 409, 270, 269, 267, 0, 37, 39, 40, 185]

import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';
import { emptyFace, type FaceFrame, type Point } from '@pose-game/core';
import type { Camera } from './camera.js';

export interface FaceTrackerOptions {
  camera: Camera;
  /**
   * Where to load the model from. Default: Google's CDN bundle.
   * For offline / privacy, host face_landmarker.task in /public.
   */
  modelAssetPath?: string;
}

// Lip ring indices (must match chomp-ios).
const INNER_LIP_INDICES = [
  78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310, 311, 312, 13, 82,
  81, 80, 191,
] as const;
const OUTER_LIP_INDICES = [
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37,
  39, 40, 185,
] as const;

// Hysteresis thresholds.
const MOUTH_OPEN_ENTER = 0.045;
const MOUTH_OPEN_EXIT = 0.03;
const SMILE_ENTER = 0.42;
const SMILE_EXIT = 0.36;

// Default model + wasm endpoints.
const DEFAULT_MODEL_PATH =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task';
const WASM_FILESET_PATH =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10/wasm';

const distance = (a: Point, b: Point): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
};

export class FaceTracker {
  private _frame: FaceFrame = emptyFace();
  private landmarker: FaceLandmarker | null = null;
  private unsubscribe: (() => void) | null = null;
  private lastTimestamp = -1;
  private mouthOpenState = false;
  private smileState = false;

  constructor(private readonly opts: FaceTrackerOptions) {}

  async load(): Promise<void> {
    if (this.landmarker != null) return;
    const fileset = await FilesetResolver.forVisionTasks(WASM_FILESET_PATH);
    this.landmarker = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: this.opts.modelAssetPath ?? DEFAULT_MODEL_PATH,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
    });
  }

  start(): void {
    if (this.unsubscribe != null) return;
    this.unsubscribe = this.opts.camera.onFrame(this.onFrame);
  }

  stop(): void {
    if (this.unsubscribe != null) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    if (this.landmarker != null) {
      this.landmarker.close();
      this.landmarker = null;
    }
    this._frame = emptyFace();
    this.mouthOpenState = false;
    this.smileState = false;
    this.lastTimestamp = -1;
  }

  get lastFrame(): FaceFrame {
    return this._frame;
  }

  private onFrame = (): void => {
    const lm = this.landmarker;
    const camera = this.opts.camera;
    if (lm == null || !camera.isReady) return;

    // detectForVideo requires monotonically increasing timestamps.
    let ts = performance.now();
    if (ts <= this.lastTimestamp) ts = this.lastTimestamp + 1;
    this.lastTimestamp = ts;

    let result: FaceLandmarkerResult;
    try {
      result = lm.detectForVideo(camera.video, ts);
    } catch {
      // MediaPipe occasionally throws if the video isn't ready yet; keep
      // serving the previous frame rather than crashing the pipeline.
      return;
    }

    const faces = result.faceLandmarks;
    if (faces.length === 0 || faces[0] == null || faces[0].length === 0) {
      this._frame = emptyFace();
      this.mouthOpenState = false;
      this.smileState = false;
      return;
    }

    this._frame = this.buildFrame(faces[0], camera.width, camera.height);
  };

  private buildFrame(
    raw: NormalizedLandmark[],
    width: number,
    height: number,
  ): FaceFrame {
    // Convert all normalized landmarks to canvas pixel space (top-left origin).
    const landmarks: Point[] = new Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      const p = raw[i];
      if (p == null) {
        landmarks[i] = { x: 0, y: 0 };
        continue;
      }
      // Mirror x so the visual feels like a selfie mirror: hand on user's
      // right side appears on the right side of the canvas.
      landmarks[i] = { x: (1 - p.x) * width, y: p.y * height };
    }

    const safePoint = (idx: number): Point => landmarks[idx] ?? { x: 0, y: 0 };

    const innerLip: Point[] = INNER_LIP_INDICES.map(safePoint);
    const outerLip: Point[] = OUTER_LIP_INDICES.map(safePoint);

    // Face height: forehead (10) to chin (152).
    const forehead = safePoint(10);
    const chin = safePoint(152);
    const faceH = Math.max(distance(forehead, chin), 1e-6);

    // Mouth-open ratio: gap (13↔14) / face height.
    const gap = distance(safePoint(13), safePoint(14));
    const mouthOpenRatio = Math.max(0, Math.min(1, gap / faceH));

    // Hysteresis on mouth-open boolean.
    const prevMouthOpen = this.mouthOpenState;
    let mouthOpen = prevMouthOpen;
    if (!prevMouthOpen && mouthOpenRatio > MOUTH_OPEN_ENTER) mouthOpen = true;
    else if (prevMouthOpen && mouthOpenRatio < MOUTH_OPEN_EXIT)
      mouthOpen = false;
    const biteEdge = mouthOpen && !prevMouthOpen;
    this.mouthOpenState = mouthOpen;

    // Smile detection: mouth width (61↔291) / face height with hysteresis.
    const mouthWidth = distance(safePoint(61), safePoint(291));
    const smileRatio = mouthWidth / faceH;
    const prevSmile = this.smileState;
    let smiling = prevSmile;
    if (!prevSmile && smileRatio > SMILE_ENTER) smiling = true;
    else if (prevSmile && smileRatio < SMILE_EXIT) smiling = false;
    this.smileState = smiling;

    // Head roll (tilt): angle of forehead→chin vs vertical.
    // Positive = head tilted to player's right (in mirrored selfie view, that
    // means the chin is to the screen's left relative to the forehead).
    const dx = chin.x - forehead.x;
    const dy = chin.y - forehead.y;
    // atan2 of (dx, dy) gives 0 when pointing straight down, positive when
    // chin drifts right of forehead in image space. Selfie cameras are
    // typically un-mirrored at the texture level; downstream renderers mirror
    // the canvas. Either way the sign matches "tilt to player's right" once
    // the video is mirrored for display.
    const headTilt = Math.atan2(dx, dy);

    // Head yaw from horizontal face-mesh asymmetry.
    // Landmarks 234 (left cheek) and 454 (right cheek) bracket the face.
    // When the head rotates right, the right cheek (454) gets closer to the
    // mid-face (nose tip 1) and the left cheek (234) gets farther.
    const leftCheek = safePoint(234);
    const rightCheek = safePoint(454);
    const noseTip = safePoint(1);
    const leftSpan = noseTip.x - leftCheek.x;
    const rightSpan = rightCheek.x - noseTip.x;
    const totalSpan = leftSpan + rightSpan;
    const asymmetry =
      totalSpan > 1e-6 ? (rightSpan - leftSpan) / totalSpan : 0;
    // Map asymmetry (-1..1) to a yaw-like angle in radians. Negative
    // asymmetry (right cheek tucked in) → head turned to player's right
    // → positive headYaw.
    const headYaw = -asymmetry * (Math.PI / 2);

    return {
      visible: true,
      landmarks,
      innerLip,
      outerLip,
      mouthOpen,
      mouthOpenRatio,
      biteEdge,
      smiling,
      headTilt,
      headYaw,
    };
  }
}
